import React, { useEffect, useState, createContext, useContext } from 'react';
import { RouterApp } from './router';
import useAxios from 'axios-hooks';
import { GlobalStyle } from 'styles/global-styles';
import '../styles/style.scss';
import '../styles/css/dx-customize.css';
import '../styles/css/dx-premium.css';
import enMessages from './../locales/en.json';
import viMessages from 'devextreme/localization/messages/vi.json';
import viCustom from './../locales/vi.json';
import koMessages from './../locales/ko.json';
import jaMessages from './../locales/ja.json';
import zhMessages from './../locales/zh.json';
import { locale, loadMessages } from 'devextreme/localization';
import config from 'devextreme/core/config';
import LoadPanel from 'app/components/LoadPanel';
import { Helmet } from 'react-helmet-async';
import Favicon from 'react-favicon';
import { APP_NAME, LANG_DEFAULT } from 'constant';
import { logo } from 'images';
import { useRecoilValue } from 'recoil';
import { systemInfoAtom } from 'store/atom-state/system-info-atom';
import { getIsLoggedIn } from 'store/atom-state/authen-atom';

const AppContext = createContext({
  myPageApp: '',
});

AppContext.displayName = 'AppContext';

export const useApp = () => {
  const context = useContext(AppContext);
  return context;
};

export function App() {
  const [voca, setVoca] = useState([]);
  const systemInfo = useRecoilValue(systemInfoAtom);
  const [firstRender, setFirstRender] = useState(true);
  const isLoggedIn = useRecoilValue(getIsLoggedIn);

  const [{ loading: loadingMultiLanguage }, fetchDictionary] = useAxios(
    {
      url: '/multi-language',
      method: 'POST',
    },
    { manual: true },
  );

  /**
   * Loading last language is stored localStorage
   * When Init app first time
   */
  useEffect(() => {
    const myPagePreferenceLocal = JSON.parse(
      localStorage.getItem('myPagePreference') || '{}',
    );
    document.documentElement.lang =
      myPagePreferenceLocal?.language || LANG_DEFAULT;
    document.documentElement.translate = false;
    locale(myPagePreferenceLocal?.language || LANG_DEFAULT);

    let meta = document.createElement('meta');
    meta.name = 'google';
    meta.content = 'notranslate';
    document.head.appendChild(meta);
  }, []);

  const getDictionary = async (lang: string) => {
    const res = await fetchDictionary({
      data: {
        type: 'ui',
        languageCode: lang,
      },
    });
    if (res?.data?.status === '200') {
      const messages = [...res?.data?.data].filter(
        o => o?.languageCode === lang,
      );
      let l: any = {};
      messages?.forEach((o, i) => {
        if (o?.translation || o?.base) {
          l[`${o?.id}-${o?.base}`] = o?.translation || o?.autoTranslation;
        }
      });
      setVoca(l);
      loadMessages({
        [lang]: l,
      });
      setTimeout(() => setFirstRender(false));
    }
  };

  useEffect(() => {
    loadMessages(enMessages);
    loadMessages(viMessages);
    loadMessages(viCustom);
    loadMessages(koMessages);
    loadMessages(jaMessages);
    loadMessages(zhMessages);
    config({
      forceIsoDateParsing: true,
    });
    setVoca(systemInfo.language);
  }, [systemInfo.language]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const myPagePreferenceLocal = JSON.parse(
      localStorage.getItem('myPagePreference') ?? '{}',
    );
    const fetchMyPage = async () => {
      const lang = myPagePreferenceLocal?.language || LANG_DEFAULT;
      await getDictionary(lang);
      document.documentElement.lang = lang;
      locale(lang);
    };
    fetchMyPage();
  }, [isLoggedIn]);

  const value: any = {
    voca,
    setVoca,
    firstRender,
    setFirstRender,
  };
  const loadingAll = loadingMultiLanguage;
  return (
    <>
      <AppContext.Provider value={value}>
        <LoadPanel visible={loadingAll} />
        <Helmet>
          <meta charSet="utf-8" />
          <title>{APP_NAME}</title>
        </Helmet>
        <Favicon url={logo || ''} />
        <RouterApp />
      </AppContext.Provider>
      <GlobalStyle />
    </>
  );
}
